import React, { DragEvent, PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { DropArea } from '@ff-os/shared-react/components/file-uploader/DropArea';
import { createFileUploaderRef, UploadError } from '@ff-os/shared-react/components/file-uploader/FileUploader';
import { Icon } from '@ff-os/shared-react/components/icons/Icon';
import { IconNames } from '@ff-os/shared-react/components/icons/Icons';
import { maximumImageSize } from '@ff-os/shared-utils/constants/maximum-file-sizes';
import { Translate } from '@ff-os/shared-utils/types/Translate';
import { PhotoPrintsPdpDispatch } from '@ff-scene-plugins/photo-prints-redux/store';
import classNames from 'classnames';
import { uploadImages } from '../../../thunks/uploadImages';
import { ChangeGroupOverlay } from '../change-group-overlay/ChangeGroupOverlay';
import styles from './PhotoPrintsGroupDropzone.module.scss';

const _PhotoPrintsGroupDropzone = ({
    children, className,
    addPictures, groupId,
    onError,
    disabled, onDragStart, onDragEnd
}: Props) => {

    const { t } = useTranslation();
    const uploaderRef = createFileUploaderRef();
    let timeout: NodeJS.Timeout;

    const [dragState, setDragState] = useState<DragState>('initial');
    const [photoId, setPhotoId] = useState<string | null>(null);
    const [isChangeGroupOpen, setChangeGroupOpen] = useState<boolean>(false);

    const isMovingFiles = (event: DragEvent) => {
        return event.dataTransfer.items[0]?.kind === 'file';
    };

    const isMovingAddon = (event: DragEvent) => {
        return event.dataTransfer.items[0]?.kind === 'string'
            && event.dataTransfer.items[0].type === 'text/plain';
    };

    const onDragOver = (event: DragEvent) => {
        if (!disabled && !isMovingAddon(event)) {
            if (isMovingFiles(event)) {
                clearTimeout(timeout);
                onDragStart();
                setDragState('dragging_files');
            } else {
                clearTimeout(timeout);
                onDragStart();
                setDragState('dragging_photo');
            }
        }
    };

    const onDrop = (event: DragEvent) => {

        event.stopPropagation();

        if (!isMovingFiles(event)) {
            const originGroupId = event.dataTransfer.getData('groupId');

            if (originGroupId !== groupId) {
                setPhotoId(event.dataTransfer.getData('photoId'));
                setChangeGroupOpen(true);
            }
            onDragEnd();
            setDragState('initial');
        }
    };

    const onDragLeave = () => {

        clearTimeout(timeout);

        // Timeout prevents leave on children from triggering initial state
        timeout = setTimeout(() => {
            onDragEnd();
            setDragState('initial');
        }, 100);
    };

    const handleChange = (files: File[]) => {
        addPictures(files);
        setDragState('initial');
    };

    const handleErrors = (errors: UploadError[]) => {
        setDragState('error');
        clearTimeout(timeout);
        onError && onError(errors);
    };

    const fileOverlayClasses = classNames(styles.overlay, {
        [styles.activeOverlay]: ['dragging_files', 'dropped', 'dragging_photo'].includes(dragState),
        [styles.dragOverlay]: dragState.includes('dragging')
    });

    return (
        <div className={classNames(className, styles.container)}>
            <DropArea ref={uploaderRef} maxSize={maximumImageSize} multiple
                onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
                onChange={handleChange} onError={handleErrors} className={styles.dropArea}>

                {children}

                <div className={fileOverlayClasses}>
                    <p className={styles.overlayContent}>
                        <Icon className={styles.overlayIcon} type={determineIconType(dragState)} />
                        {determineOverlay(t, dragState)}
                    </p>
                </div>

                {isChangeGroupOpen && (
                    <ChangeGroupOverlay groupId={groupId}
                        photoId={photoId!}
                        close={() => setChangeGroupOpen(false)} />
                )}
            </DropArea>
        </div>
    );
};

type OwnProps = PropsWithChildren<{
    groupId: string;
    className?: string;
    disabled: boolean;
    onError?: (errors: UploadError[]) => void;
    onDragStart: () => void;
    onDragEnd: () => void;
}>;

type Props = OwnProps & {
    addPictures: (files: File[]) => void;
};

type DragState = 'initial' | 'dragging_files' | 'dragging_photo' | 'dropped' | 'error';

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, ownProps: OwnProps) => ({
    addPictures: (files: File[]) => {
        dispatch(uploadImages(files, ownProps.groupId));
    }
});

export const PhotoPrintsGroupDropzone = connect(
    null,
    mapDispatchToProps
)(_PhotoPrintsGroupDropzone);

const determineOverlay = (t: Translate, dragState: DragState) => {
    if (dragState === 'dragging_files') {
        return t('dropzone.drop_to_upload');
    } else if (dragState === 'dragging_photo') {
        return t('dropzone.drop_to_move');
    } else if (dragState === 'dropped') {
        return t('dropzone.ready');
    } else if (dragState === 'error') {
        return t('dropzone.error.generic');
    }
};

const iconByState: Record<DragState, IconNames> = {
    'initial': 'image-file-upload',
    'dropped': 'alert-circle-check',
    'dragging_files': 'image-file-upload',
    'dragging_photo': 'drag-drop',
    'error': 'alert-circle-exclamation'
};

const determineIconType = (dragState: DragState) => iconByState[dragState];